import { Card, CircleButton, useForm, withStyles } from 'vopay';

import useGenerateEmbedUrl from '#shared/hooks/useApi/useGenerateEmbedUrl';
import useSendPaylink from '#shared/hooks/useApi/useSendPaylink';
import DialogLink from '../DialogLink';

import {
  useElinxRequestNameRows,
  useElinxRequestMethodRows,
  useElinxRequestDetailRows,
  FormValues,
  SerializedFormValues,
  buildInitialValues,
} from './buildFormConfig';

import styles from './ElinxRequestDialog.module.scss';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import Dialog from '#shared/components/Dialog/Dialog';
import { useTranslation } from 'react-i18next';

import { Button, Title } from '#shared/components/UI';

const cn = withStyles(styles);

type ElinxRequestProps = {
  accountName: string;
  accountID: string;
  email: string;
  onClose: () => void;
  // Host facts the portal read from its ClientData provider.
  clientAccountsEnabled: boolean;
  country: string;
  // The signed-in account's own name, used as the request's default sender name.
  senderName?: string;
  lockedSelectOptions?: { [featureKey: string]: string };
  onLockedFeature?: (featureKey: string) => void;
  // Captured from useHostNavigate() by the opener — the dialog renders under
  // the DialogProvider, above the HostNavigationProvider, so the context
  // can't be read here directly.
  hostNavigate?: (path: string) => void;
};

const ElinxRequestDialogue = ({
  accountName,
  accountID,
  email,
  onClose,
  clientAccountsEnabled,
  country,
  senderName = '',
  lockedSelectOptions,
  onLockedFeature,
  hostNavigate,
}: ElinxRequestProps) => {
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { generateEmbedUrl } = useGenerateEmbedUrl();
  const { sendPaylink } = useSendPaylink();
  const initialValues = buildInitialValues(accountName, senderName);
  const sendElinxNameRows = useElinxRequestNameRows();
  const sendElinxMethodRows = useElinxRequestMethodRows({ country, lockedSelectOptions, onLockedFeature });
  const sendElinxDetailRows = useElinxRequestDetailRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.ElinxRequestDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });

  const { renderFormElements, ctx } = useForm<FormValues, SerializedFormValues>({
    initialValues: initialValues,
    config: [...sendElinxNameRows, ...sendElinxMethodRows, ...sendElinxDetailRows],
    onSubmit: async (values) => {
      try {
        const sendPaylinkRes = await sendRequest(values);

        const successChoice = await alertDialog.success(
          t('Success.title', { defaultValue: 'eLinx Connect Request successfully sent!' }),
          <div className={cn('sendRequestSuccessDetails')}>
            {t('Success.content', {
              defaultValue:
                'The connection request will be sent to {{accountName}} to connect their Payment Method. Once the request is complete you can start processing EFT transactions with this client account.',
              accountName,
            })}

            <DialogLink
              label={t('Success.dialogLink', { defaultValue: 'eLinx Embedded Link' })}
              link={sendPaylinkRes.Link}
            />
          </div>,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
            // "View Request Details" is only offered when the host can navigate.
            ...(hostNavigate
              ? { confirmLabel: t('Success.confirmLabel', { defaultValue: 'View Request Details' }) }
              : {}),
          },
        );

        if (successChoice === 'confirm') {
          // Link-out via the host's onNavigate. (dropped router state: eLinx;
          // the portal also switched its sub-nav to paylinkHistory)
          hostNavigate?.(`/elinx-history/details/${sendPaylinkRes.ELinxRequestID}`);
        } else {
          ctx.resetForm();
        }

        onClose();
      } catch (err) {
        if (err instanceof Error) {
          alertDialog.error(
            t('Error.title', { defaultValue: 'Error! The eLinx Connect Request was not sent.' }),
            err.message,
            {
              abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
            },
          );
        }
      }
    },
  });

  async function sendRequest(values: SerializedFormValues) {
    try {
      blockingLoader.show();

      const embedUrlRes = await generateEmbedUrl({
        RedirectURL: 'https://request-dev.vopay.com/',
        Language: 'en',
        RedirectMethod: 'JavascriptMessage',
        PaymentSelectionMethod: values.PaymentSelectionMethod,
        AccountSelectionMethod: values.AccountSelectionMethod,
        ClientControlled: clientAccountsEnabled,
        ClientReferenceNumber: '',
        TermsAndConditions: '',
        Country: country,
        ClientAccountID: accountID,
      });

      const sendPaylinkRes = await sendPaylink({
        SenderName: values.SenderName,
        ReceiverName: values.ReceiverName,
        ReceiverEmailAddress: email,
        IframeKey: embedUrlRes.IframeKey,
        InvoiceNumber: values.InvoiceNumber,
        Note: values.Note,
        RecipientMessage: values.Note,
        ClientAccountID: accountID,
        SendNotification: values.SendEmail,
        NotificationMethod: 'email',
      });

      return sendPaylinkRes;
    } finally {
      blockingLoader.hide();
    }
  }

  return (
    <Card className={cn('elinxRequestContainer')}>
      <CircleButton icon="close" onClick={onClose} className={cn('closeButton')} circleFill={false} size="large" />

      <form onSubmit={ctx.handleSubmit} className={cn('form')}>
        <Dialog.Title level="4">{t('title', { defaultValue: 'Send eLinx Request' })}</Dialog.Title>
        <div className={cn('row')}>{renderFormElements({ config: sendElinxNameRows })}</div>
        <Title level="5">{t('details', { defaultValue: 'eLinx Details' })}</Title>
        <div className={cn('row')}>{renderFormElements({ config: sendElinxMethodRows })}</div>
        {renderFormElements({ config: sendElinxDetailRows })}

        <Button
          colorScheme="primary"
          type="submit"
          disabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
          style={{
            marginLeft: 'auto',
          }}
        >
          {t('submitButton', { defaultValue: 'Send eLinx Request' })}
        </Button>
      </form>
    </Card>
  );
};

export default ElinxRequestDialogue;
